import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Button, ConfigProvider, Form, Input, Modal, Table, notification, Spin } from 'antd'
import { phoneValidate } from '../../../../plugins/validates'
import { updatePositionCheckPerson, updatePositionRecheckPerson } from '../../../../common/request'
import ResponseCode from '../../../../common/response-code'
import { getUid } from '../../../../plugins/utils'

const PERSON_TYPE_CONFIG = {
  check: {
    title: '配置审核人员',
    batchTitle: '批量配置审核人员',
    listKey: 'checkPersonList',
    saveRequest: updatePositionCheckPerson,
    saveFailMessage: '保存审核人员配置失败'
  },
  recheck: {
    title: '配置复审人员',
    batchTitle: '批量配置复审人员',
    listKey: 'recheckPersonList',
    saveRequest: updatePositionRecheckPerson,
    saveFailMessage: '保存复审人员配置失败'
  }
}

const EditCheckPersonModal = forwardRef((props, ref) => {
  const personType = props.personType || 'check'
  const typeConfig = PERSON_TYPE_CONFIG[personType] || PERSON_TYPE_CONFIG.check

  const [form] = Form.useForm()
  const [notifyApi, contentHolder] = notification.useNotification()
  const [modalVisible, setModalVisible] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [tableData, setTableData] = useState([])

  useImperativeHandle(ref, () => {
    return {
      showModal
    }
  })

  useEffect(() => {
    if (modalVisible) {
      const config = PERSON_TYPE_CONFIG[personType] || PERSON_TYPE_CONFIG.check
      if (props.positionItemList.length > 1) {
        setModalTitle(config.batchTitle)
        setTableData([])
      } else {
        setModalTitle(config.title)
        const personList = props.positionItemList[0][config.listKey] || []
        setTableData(personList.map(item => {
          return {
            ...item,
            id: getUid()
          }
        }))
      }
    }
  }, [modalVisible, personType, props.positionItemList])

  const columns = [
    {
      title: '姓名',
      dataIndex: 'checkName',
      key: 'checkName'
    },
    {
      title: '手机号',
      dataIndex: 'checkPhone',
      key: 'checkPhone'
    },
    {
      title: '操作',
      dataIndex: 'operation',
      width: '80px',
      key: 'operation',
      render: (record, row) => {
        return (
          <div className={ 'd-flex align-items-center justify-content-end' }>
            <Button
              ghost
              danger
              type={ 'primary' }
              onClick={ deleteCheckPerson.bind(this, row) }
            >删除</Button>
          </div>
        )
      }
    }
  ]

  const showModal = () => {
    setModalVisible(true)
  }

  const hideModal = () => {
    setModalVisible(false)
    form.resetFields()
    setTableData([])
  }

  const deleteCheckPerson = (row) => {
    setTableData(tableData.filter(item => {
      return row.checkPhone !== item.checkPhone
    }))
  }

  const addCheckPerson = () => {
    form.validateFields().then(fields => {
      if (tableData.find(item => {
        return item.checkPhone === fields.checkPhone
      })) {
        Modal.error({
          title: '错误',
          content: '手机号已存在，请勿重复添加'
        })
        return
      }
      setTableData([].concat(tableData, [{
        ...fields,
        id: getUid()
      }]))
      form.resetFields()
    }).catch(err => {
      console.error(err)
    })
  }

  const onOk = () => {
    setModalLoading(true)

    const params = {
      checkPersonList: tableData,
      examId: props.examInfo.id,
      ids: props.positionItemList.map(item => {
        return item._id
      })
    }

    typeConfig.saveRequest(params).then(res => {
      setModalLoading(false)
      if (res.code === ResponseCode.success) {
        notifyApi.success({
          message: '操作成功'
        })
        hideModal()
        props.insertSuccess && props.insertSuccess()
      } else {
        Modal.error({
          title: '错误',
          content: res.msg,
          centered: true
        })
      }
    }).catch(() => {
      setModalLoading(false)
      Modal.error({
        title: '网络错误',
        content: typeConfig.saveFailMessage,
        centered: true
      })
    })
  }

  const handleReset = () => {
    if (props.positionItemList.length > 1) {
      setTableData([])
    } else {
      const personList = props.positionItemList[0][typeConfig.listKey] || []
      setTableData(personList.map(item => {
        return {
          ...item,
          id: getUid()
        }
      }))
    }
  }

  return (
    <div ref={ ref }>
      { contentHolder }

      <Modal
        title={ modalTitle }
        width={ '800px' }
        open={ modalVisible }
        onOk={ onOk }
        okText={ '保存' }
        onCancel={ hideModal }
        footer={ [
          <Button key="back" onClick={ hideModal }>
            取消
          </Button>,
          <Button key="reset" type="primary" ghost onClick={ handleReset }>
            重置
          </Button>,
          <Button key="save" type="primary" loading={ modalLoading } onClick={ onOk }>
            保存
          </Button>
        ] }
      >
        <Spin spinning={ modalLoading }>
          <ConfigProvider componentSize={ 'small' }>
            <div className={ 'my-3 d-flex' }>
              <Form
                form={ form }
                layout={ 'inline' }
              >
                <Form.Item
                  name={ 'checkName' }
                  label={ '姓名' }
                  rules={ [{ required: true, message: '姓名不能为空' }] }
                >
                  <Input placeholder={ '请输入姓名' }></Input>
                </Form.Item>
                <Form.Item
                  name={ 'checkPhone' }
                  label={ '手机号' }
                  required
                  rules={ [{ validator: phoneValidate }] }
                >
                  <Input placeholder={ '请输入手机号' }></Input>
                </Form.Item>
              </Form>

              <Button
                type={ 'primary' }
                onClick={ addCheckPerson }
              >添加</Button>
            </div>

            <Table
              columns={ columns }
              dataSource={ tableData }
              rowKey={ (record) => record.id }
              pagination={ false }
            ></Table>
          </ConfigProvider>
        </Spin>
      </Modal>
    </div>
  )
})

export default EditCheckPersonModal
